import { Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useCallback, type FC } from "react";

export type AiImageStyle = "illustration" | "realistic" | "3d" | "line-art";
export const AiImageStyleConfig = {
  illustration: {
    name: "Illustration",
    referenceImageSrc:
      "https://images.changeengine.com/2286f56c-57aa-4313-bc88-fde38caaa844/020392ec-b61c-4424-8183-5261140beb32",
  },
  realistic: {
    name: "Realistic",
    referenceImageSrc:
      "https://images.changeengine.com/2286f56c-57aa-4313-bc88-fde38caaa844/b922b767-2a7f-477b-874f-86e821f89cc4",
  },
  "3d": {
    name: "3D",
    referenceImageSrc:
      "https://images.changeengine.com/2286f56c-57aa-4313-bc88-fde38caaa844/1d6aa74c-f91a-4950-adac-53d207d59741",
  },
  "line-art": {
    name: "Line art",
    referenceImageSrc:
      "https://images.changeengine.com/2286f56c-57aa-4313-bc88-fde38caaa844/8592ec41-ff9d-4cd8-9f0d-66b668a4e037",
  },
};

export const AiImageStyleToggle: FC<{
  aiImageStyle?: string;
  onChange: (style: string) => void;
}> = ({ aiImageStyle, onChange }) => {
  const handleChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newStyle: string) => {
      onChange(newStyle);
    },
    [onChange],
  );

  return (
    <ToggleButtonGroup
      color="primary"
      value={aiImageStyle}
      exclusive
      onChange={handleChange}
      aria-label="Default AI image style"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        columnGap: 0.5,
        rowGap: 0.5,
        "& .MuiToggleButtonGroup-grouped": {
          padding: 0.5,
          border: "1px solid transparent",
          "&:not(:first-of-type)": { borderRadius: 1 },
          "&:first-of-type": { borderRadius: 1 },
        },
      }}
    >
      <AiImageStyleToggleButton
        analyticsId="default-ai-image-style-illustration"
        style="illustration"
      />
      <AiImageStyleToggleButton analyticsId="default-ai-image-style-realistic" style="realistic" />
      <AiImageStyleToggleButton analyticsId="default-ai-image-style-3d" style="3d" />
      <AiImageStyleToggleButton analyticsId="default-ai-image-style-line-art" style="line-art" />
    </ToggleButtonGroup>
  );
};

const AiImageStyleToggleButton: FC<{
  analyticsId: string;
  style: AiImageStyle;
}> = ({ analyticsId, style }) => (
  <ToggleButton data-analytics-id={analyticsId} value={style} sx={{ textTransform: "none" }}>
    <Stack alignItems="center">
      <img
        src={AiImageStyleConfig[style].referenceImageSrc}
        width="76px"
        height="76px"
        alt={AiImageStyleConfig[style].name}
        style={{ objectFit: "contain", borderRadius: "12px" }}
      />
      <Typography variant="body2">{AiImageStyleConfig[style].name}</Typography>
    </Stack>
  </ToggleButton>
);
